import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { listAllMedia } from '../api';
import type { ContentMedia } from '../types';
import './MediaPage.css';

type MediaPageProps = {
  workspaceId: number;
};

function DownloadIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10 3v10M6 9l4 4 4-4" />
      <path d="M3 16h14" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M11 4h5v5" />
      <path d="M16 4l-8 8" />
      <path d="M9 5H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-4" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M4 4l12 12M16 4L4 16" />
    </svg>
  );
}

function MediaLightbox({
  media,
  onClose,
  onNavigateToPost,
}: {
  media: ContentMedia;
  onClose: () => void;
  onNavigateToPost: () => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleDownload = async () => {
    if (!media.media_url) return;
    try {
      const res = await fetch(media.media_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = media.media_type === 'video' ? 'mp4' : 'png';
      a.download = `media-${media.id}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(media.media_url, '_blank');
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="media-lightbox" onClick={(e) => e.stopPropagation()}>
        <div className="media-lightbox__header">
          <div className="media-lightbox__actions">
            <button
              className="media-lightbox__btn"
              onClick={handleDownload}
              title="Download"
            >
              <DownloadIcon />
              Download
            </button>
            {media.outline_id && (
              <button
                className="media-lightbox__btn media-lightbox__btn--secondary"
                onClick={onNavigateToPost}
                title="Go to post"
              >
                <ExternalLinkIcon />
                Go to post
              </button>
            )}
          </div>
          <button className="modal-close" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </div>
        <div className="media-lightbox__body">
          {media.media_type === 'video' ? (
            <video
              src={media.media_url}
              controls
              className="media-lightbox__asset"
            />
          ) : (
            <img
              src={media.media_url}
              alt={media.prompt ?? 'Media'}
              className="media-lightbox__asset"
            />
          )}
        </div>
        {media.prompt && (
          <div className="media-lightbox__prompt">{media.prompt}</div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function MediaPage({ workspaceId }: MediaPageProps) {
  const [mediaItems, setMediaItems] = useState<ContentMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<ContentMedia | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    listAllMedia(workspaceId)
      .then((data) => {
        if (!mounted) return;
        // Show every media asset that was actually generated (has a URL).
        // Hidden slides and media on superseded/deleted items still appear so the
        // workspace's full generation history is visible in one place.
        setMediaItems((data.results ?? []).filter((m) => m.media_url));
      })
      .catch(() => {
        if (!mounted) return;
        setError('Failed to load media.');
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [workspaceId]);

  const handleNavigateToPost = useCallback(() => {
    if (!selected?.outline_id) return;
    setSelected(null);
    navigate(`/content/${selected.outline_id}`);
  }, [selected, navigate]);

  if (loading) {
    return (
      <div className="media-page">
        <div className="page-header">
          <h1 className="page-header__title">Media</h1>
        </div>
        <div className="media-page__loading">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="media-page">
        <div className="page-header">
          <h1 className="page-header__title">Media</h1>
        </div>
        <div className="media-page__error">{error}</div>
      </div>
    );
  }

  return (
    <div className="media-page">
      <div className="page-header">
        <h1 className="page-header__title">Media</h1>
        <p className="page-header__subtitle">{mediaItems.length} generated assets</p>
      </div>

      {mediaItems.length === 0 ? (
        <div className="media-page__empty">
          No media generated yet. Create some content and generate images or videos.
        </div>
      ) : (
        <div className="media-grid">
          {mediaItems.map((item) => (
            <button
              key={item.id}
              className="media-card"
              onClick={() => setSelected(item)}
              title={item.prompt ?? undefined}
            >
              {item.media_type === 'video' ? (
                <video src={item.media_url} className="media-card__thumb" muted />
              ) : (
                <img
                  src={item.media_url}
                  alt={item.prompt ?? 'Media'}
                  className="media-card__thumb"
                  loading="lazy"
                />
              )}
              <div className="media-card__overlay">
                <DownloadIcon />
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <MediaLightbox
          media={selected}
          onClose={() => setSelected(null)}
          onNavigateToPost={handleNavigateToPost}
        />
      )}
    </div>
  );
}
